/**
 * Raster API client -- sidecar grouping, presigned URL uploads with
 * byte-level progress tracking, and processing triggers.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UploadUrlEntry {
  filename: string;
  s3_key: string;
  upload_url: string;
}

export interface UploadUrlsResponse {
  job_id: string;
  upload_urls: UploadUrlEntry[];
}

export interface ProcessResponse {
  job_id: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Sidecar grouping
// ---------------------------------------------------------------------------

/** Primary raster extensions (case-insensitive match). */
const PRIMARY_EXTENSIONS = /\.(tif|tiff|jp2)$/i;

/** Sidecar extensions keyed by lowercased primary extension. */
const SIDECAR_EXTENSIONS: Record<string, string[]> = {
  '.tif':  ['.tfw', '.prj', '.tifw'],
  '.tiff': ['.tfw', '.prj', '.tifw'],
  '.jp2':  ['.j2w', '.prj'],
};

/**
 * Extract the basename stem (no extension) from a filename.
 * e.g. "Oakland.TIF" -> "oakland"
 */
function stem(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return (dot > 0 ? filename.slice(0, dot) : filename).toLowerCase();
}

function extLower(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot).toLowerCase() : '';
}

/**
 * Group a list of files into a primary raster and its matching sidecars.
 *
 * Returns `null` if no primary raster file is found.
 */
export function groupRasterFiles(
  files: FileList | File[],
): { primary: File; sidecars: File[] } | null {
  const arr = Array.from(files);

  // Find the first primary raster file
  const primary = arr.find((f) => PRIMARY_EXTENSIONS.test(f.name));
  if (!primary) return null;

  const baseStem = stem(primary.name);
  const primaryExt = extLower(primary.name);
  const allowedSidecars = SIDECAR_EXTENSIONS[primaryExt] || [];

  const sidecars = arr.filter((f) => {
    if (f === primary) return false;
    const fExt = extLower(f.name);
    return stem(f.name) === baseStem && allowedSidecars.includes(fExt);
  });

  return { primary, sidecars };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Request presigned upload URLs for a set of filenames.
 *
 * POST /api/raster/upload-url
 */
export async function requestUploadUrls(
  filenames: string[],
): Promise<UploadUrlsResponse> {
  const res = await fetch(`${API_BASE}/api/raster/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files: filenames.map((f) => ({ filename: f })) }),
  });
  if (!res.ok) {
    throw new Error(`requestUploadUrls failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Upload a single file to a presigned S3 URL with progress tracking.
 *
 * Uses XMLHttpRequest (not fetch) because fetch does not expose upload
 * progress events. Does NOT set Content-Type to avoid presigned URL
 * signature mismatch.
 */
export function uploadFileWithProgress(
  file: File,
  url: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) {
        onProgress(e.loaded, e.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'));
    });

    // Override Content-Type to match what the presigned URL expects.
    // The URL was signed without ContentType, so we send application/octet-stream
    // which S3 treats as the default when no ContentType is specified in signing.
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.send(file);
  });
}

/**
 * Upload multiple files in parallel with aggregated progress (0-100).
 */
export async function uploadFilesWithProgress(
  files: Array<{ file: File; url: string }>,
  onProgress: (percent: number) => void,
): Promise<void> {
  const totalBytes = files.reduce((sum, { file }) => sum + file.size, 0);
  const loadedMap = new Map<number, number>();

  const uploads = files.map(({ file, url }, index) =>
    uploadFileWithProgress(file, url, (loaded) => {
      loadedMap.set(index, loaded);
      let totalLoaded = 0;
      loadedMap.forEach((v) => { totalLoaded += v; });
      onProgress(totalBytes > 0 ? Math.round((totalLoaded / totalBytes) * 100) : 100);
    }),
  );

  await Promise.all(uploads);
}

/**
 * Start raster processing for an uploaded job.
 *
 * POST /api/raster/process
 */
export async function startRasterProcessing(
  jobId: string,
): Promise<ProcessResponse> {
  const res = await fetch(`${API_BASE}/api/raster/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ job_id: jobId }),
  });
  if (!res.ok) {
    throw new Error(`startRasterProcessing failed: ${res.status}`);
  }
  return res.json();
}
